import { useEffect, useRef, useState } from "react";

const CHAR_INTERVAL_MS = 22;
const LINE_STAGGER_MS = 380;

export const useLineTyping = (lines: string[], shouldStart: boolean) => {
  const [typed, setTyped] = useState<string[]>(lines.map(() => ""));
  const [allDone, setAllDone] = useState(false);
  const intervalsRef = useRef<ReturnType<typeof setInterval>[]>([]);
  const startTimeRef = useRef(0);
  const doneRef = useRef(0);

  useEffect(() => {
    if (!shouldStart) return;
    // Reset is intentional: effect re-runs when shouldStart/lines.length change
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTyped(lines.map(() => ""));

    setAllDone(false);
    doneRef.current = 0;
    startTimeRef.current = Date.now();

    const lineIndices = lines.map(() => 0);

    const advance = () => {
      const now = Date.now();
      const globalElapsed = now - startTimeRef.current;
      let changed = false;

      lines.forEach((line, lineIdx) => {
        if (lineIndices[lineIdx] >= line.length) return;
        const lineElapsed = globalElapsed - lineIdx * LINE_STAGGER_MS;
        if (lineElapsed <= 0) return;
        const target = Math.min(
          Math.floor(lineElapsed / CHAR_INTERVAL_MS),
          line.length
        );
        if (target > lineIndices[lineIdx]) {
          lineIndices[lineIdx] = target;
          changed = true;
        }
      });

      if (changed) {
        setTyped(lines.map((line, i) => line.slice(0, lineIndices[i])));
        const newDone = lineIndices.filter(
          (idx, i) => idx >= lines[i].length
        ).length;
        if (newDone === lines.length && doneRef.current < lines.length) {
          doneRef.current = newDone;
          setAllDone(true);
        }
      }
    };

    const iv = setInterval(advance, CHAR_INTERVAL_MS);
    intervalsRef.current.push(iv);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        advance();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [shouldStart, lines.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return { typed, allDone };
};
